<template>
  <section v-if="render" class="mc-dashboard__widget mt-3">
    <div class="mc-dashboard__widget-header d-flex justify-content-between">
      <div class="mt-3">
        <h4 class="font-size-16 teal-400 mb-1">
          {{ $t('dashboard.title.tripInfoAnalysis') }}
          <i id="analysisTrip" class="mc-fa icon-outline_info"></i>
          <b-tooltip target="analysisTrip" triggers="hover" placement="topleft">
            {{ $t('dashboard.summary.tooltip.tripInfoAnalysis') }}
          </b-tooltip>
        </h4>
        <p class="font-size-10 teal-400 mb-0">
          {{ periodLabel }}
        </p>
      </div>
      <div class="d-flex">
        <date-picker
          ref="periodMonthPicker"
          v-model="period"
          type="month"
          format="MMM YYYY"
          value-type="YYYY-MM-DD"
          placeholder="Periode"
          :confirm="true"
          :confirm-text="$t('global.action.apply')"
          :disabled-date="disabledRange"
          :lang="datePickerLang"
          @pick="pickDate"
        >
          <!-- @confirm="changeDate" -->
          <template #footer>
            <b-button class="mx-2" variant="outline-primary" @click="closeCalendar()">
              {{ $t('global.action.cancel') }}
            </b-button>
          </template>
        </date-picker>
      </div>
    </div>
    <b-row v-if="render">
      <b-col md="12">
        <AnalysisTrip
          :loading="loading || !tripsStatisticsIsReady"
          :tripsStatistics="tripsStatistics"
        />
      </b-col>
      <b-col md="4" class="mb-4">
        <TotalDrivingDuration
          :loading="loading || !tripsStatisticsIsReady"
          :tripsStatistics="tripsStatistics"
          :period="period"
        />
      </b-col>
      <b-col v-if="false" md="4" class="mb-4">
        <!-- masih belum ada datanya -->
        <ComparisonFuel
          :loading="loading || !tripsStatisticsIsReady"
          :tripsStatistics="tripsStatistics"
        />
      </b-col>
      <b-col md="4" class="mb-4">
        <TotalDrivingDistance
          :loading="loading || !tripsStatisticsIsReady"
          :tripsStatistics="tripsStatistics"
          :period="period"
        />
      </b-col>
      <b-col md="4" class="mb-4">
        <Top20UseFuel
          :loading="loading || !tripsStatisticsIsReady"
          :tripsStatistics="tripsStatistics"
        />
      </b-col>
      <b-col md="4" class="mb-4">
        <Top20DrivingDistance
          :loading="loading || !tripsStatisticsIsReady"
          :tripsStatistics="tripsStatistics"
          :period="period"
        />
      </b-col>
    </b-row>
  </section>
</template>

<script>
import moment from 'moment'
import AnalysisTrip from './AnalysisTrip.vue'
import TotalDrivingDuration from './TotalDrivingDuration.vue'
import ComparisonFuel from './ComparisonFuel.vue'
import TotalDrivingDistance from './TotalDrivingDistance.vue'
import Top20UseFuel from './Top20UseFuel.vue'
import Top20DrivingDistance from './Top20DrivingDistance.vue'
import { GET_TRIPS_STATISTICS } from '@/services/api/dashboard/dashboard'
import datePickerLang from '@/helpers/date-picker-lang'
import { Helper } from '@/mixin/helper'
import { mapGetters } from 'vuex'

moment.locale('id')

export default {
  components: {
    AnalysisTrip,
    TotalDrivingDuration,
    ComparisonFuel,
    TotalDrivingDistance,
    Top20UseFuel,
    Top20DrivingDistance,
  },
  mixins: [Helper],
  apollo: {
    tripsStatistics: {
      client: 'clientLowPrio',
      query: GET_TRIPS_STATISTICS,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          filter: {
            useNewQuery: this.getFeatureFlagData('optimizeDashboardSummary').value,
            start: {
              month: moment(this.period).locale('en').subtract(2, 'months').format('MMMM'),
              year: Number(moment(this.period).subtract(2, 'months').format('YYYY')),
            },
            end: {
              month: moment(this.period).locale('en').format('MMMM'),
              year: Number(moment(this.period).format('YYYY')),
            },
            ...(this.fleetTypes.length > 0
              ? { vehicleTypeId: this.fleetTypes.map((item) => item.vehicleTypeId) }
              : {}),
            ...(this.fleetGroups.length > 0
              ? { vgroupId: this.fleetGroups.map((item) => item.id) }
              : {}),
            ...(this.branches.length > 0
              ? { companyIds: this.branches.map((item) => item.id) }
              : {}),
          },
        }
      },
      error(error) {
        if (error.networkError && error.networkError.statusCode === 504) {
          this.$mcToast.error(this.$t('global.somethingWentWrong'))
        }
      },
      result({ data, loading }) {
        if (!loading && data && data.tripsStatistics) {
          this.tripsStatistics = data.tripsStatistics.tripsStatistics
          this.tripsStatistics.data.forEach((el) => {
            el.month = this.localizeMonth(el.month)
          })
          this.render = true

          setTimeout(() => {
            this.loading = false
          }, 1000)
        }
      },
      watchLoading(loading) {
        if (loading) {
          this.loading = true
        }
      },
    },
  },

  props: ['fleetGroups', 'fleetTypes', 'mainPeriod', 'branches'],

  data() {
    return {
      period: moment().subtract(1, 'months').format('YYYY-MM-DD'),
      periodLabel: null,
      loading: true,
      tripsStatistics: {},
      render: true,
    }
  },

  computed: {
    ...mapGetters({
      getFeatureFlagData: 'identity/getFeatureFlagData',
    }),
    datePickerLang() {
      return datePickerLang()
    },
    tripsStatisticsIsReady() {
      return (
        this.tripsStatistics && this.tripsStatistics.data && this.tripsStatistics.data.length > 0
      )
    },
  },

  watch: {
    mainPeriod: {
      handler() {
        this.period = this.mainPeriod
        const month = this.localizeMonth(moment(this.period).format('MMMM'))
        const year = moment(this.period).format('YYYY')
        this.periodLabel = `${month} ${year}`
      },
    },
    period: {
      handler() {
        const month = this.localizeMonth(moment(this.period).format('MMMM'))
        const year = moment(this.period).format('YYYY')
        this.periodLabel = `${month} ${year}`
        setTimeout(() => {
          this.loading = true
        }, 1000)
      },
    },
    fleetGroups: {
      handler() {
        setTimeout(() => {
          this.loading = true
        }, 700)
        this.loading = false
      },
    },
    fleetTypes: {
      handler() {
        setTimeout(() => {
          this.loading = true
        }, 700)
        this.loading = false
      },
    },
    branches: {
      handler() {
        setTimeout(() => {
          this.loading = true
        }, 700)
        this.loaded = false
      },
      deep: true,
    },
    '$i18n.locale': function () {
      this.render = false
      const month = this.localizeMonth(moment(this.period).format('MMMM'))
      const year = moment(this.period).format('YYYY')
      this.periodLabel = `${month} ${year}`
      setTimeout(() => {
        this.render = true
      }, 600)
    },
  },
  mounted() {
    const month = this.localizeMonth(moment(this.period).format('MMMM'))
    const year = moment(this.period).format('YYYY')
    this.periodLabel = `${month} ${year}`
  },
  methods: {
    openDatepicker() {
      this.$refs.periodMonthPicker.openPopup()
    },
    closeCalendar() {
      this.$refs.periodMonthPicker.defaultOpen = false
    },
    disabledRange: function (date) {
      return date >= moment().subtract(1, 'months').endOf('month')
    },
    pickDate(date) {
      const month = this.localizeMonth(moment(date).format('MMMM'))
      const year = moment(date).format('YYYY')
      this.periodLabel = `${month} ${year}`
    },
  },
}
</script>
